import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCustomerType } from '@/hooks/useCustomerType';
import {
  customerLabel,
  ENGINEER_VM_TIERS,
  EngineerVMTier,
  useLaunchEngineerVM,
} from '@/hooks/useEngineerVMs';
import { useAuth } from '@/state/auth';

interface LaunchVMDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LaunchVMDialog({ open, onOpenChange }: LaunchVMDialogProps) {
  const { me, whoami } = useAuth();
  const [tier, setTier] = useState<EngineerVMTier>('light');
  // Empty => the caller's own tenant. The account header targets whichever
  // tenant is picked; launching requires admin-in-that-tenant (backend-enforced).
  const [tenant, setTenant] = useState<string>('');
  const { mutateAsync, isPending } = useLaunchEngineerVM();

  // Same population as the Global Dashboard: the caller's own tenant plus every
  // real customer (an accessible account that carries a customer_type).
  const typeQuery = useCustomerType(true);
  const customers = useMemo(() => {
    const accessible = (whoami?.accounts ?? []).filter(email => email !== me);
    return typeQuery.status === 'success'
      ? accessible.filter(email => typeQuery.dataMap.has(email))
      : accessible;
  }, [whoami?.accounts, me, typeQuery.status, typeQuery.dataMap]);

  const target = tenant || me;

  const submit = async () => {
    try {
      const vm = await mutateAsync({ tier, tenant: target });
      toast.success(
        `Launching engineer VM ${vm.vm_id} for ${target}. It reaches "running" in a couple minutes.`
      );
      onOpenChange(false);
    } catch (error) {
      // Surface the backend refusal (403 admin-in-tenant gate, 409 conflict) —
      // the API wrapper puts the response text on `.name`. Keep the dialog
      // open so the user can retry.
      toast.error(
        error instanceof Error && error.name
          ? error.name
          : 'Failed to launch engineer VM.'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Launch engineer VM</DialogTitle>
          <DialogDescription>
            An ad-hoc cloud workspace bound to you in the selected customer
            tenant.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>Customer</Label>
            <Select value={target} onValueChange={setTenant}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={me}>{customerLabel(me, me)}</SelectItem>
                {customers.map(email => (
                  <SelectItem key={email} value={email}>
                    {email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Tier</Label>
            <Select
              value={tier}
              onValueChange={v => setTier(v as EngineerVMTier)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENGINEER_VM_TIERS.map(t => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? 'Launching…' : 'Launch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
